import React, { useMemo } from "react";

import ReactDatePicker from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";
import "./style.scss";

type DatePickerProps = {
  label?: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  min?: string;
  max?: string;
  infoText?: string;
};

const parseDateValue = (value?: string): Date | null => {
  if (!value?.trim()) return null;

  const isoMatch = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const DatePicker: React.FC<DatePickerProps> = ({
  label,
  name,
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  className = "",
  placeholder = "MM/DD/YYYY",
  min,
  max,
  infoText,
}) => {
  const selectedDate = useMemo(() => parseDateValue(value), [value]);
  const minDate = useMemo(() => parseDateValue(min), [min]);
  const maxDate = useMemo(() => parseDateValue(max), [max]);

  const handleChange = (date: Date | null) => {
    onChange({
      target: {
        name,
        value: date ? formatDateValue(date) : "",
      },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <div className={`${className} custom-input relative date-picker z-[2]`}>
      {label && (
        <label htmlFor={name} className="custom-input__label">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <ReactDatePicker
        id={name}
        name={name}
        selected={selectedDate}
        onChange={handleChange}
        dateFormat="MM/dd/yyyy"
        placeholderText={placeholder}
        disabled={disabled}
        minDate={minDate ?? undefined}
        maxDate={maxDate ?? undefined}
        showPopperArrow={false}
        popperPlacement="bottom-start"
        className={`custom-input__container custom-input__height date-picker__input w-full ${error ? "border-red-500" : ""}`}
        wrapperClassName="date-picker__wrapper w-full"
        calendarClassName="date-picker__calendar"
        autoComplete="off"
      />
      {required && (
        <input
          tabIndex={-1}
          aria-hidden
          value={value}
          onChange={() => undefined}
          required={required}
          className="date-picker__required-proxy"
        />
      )}
      {error && <div className="text-red-500 text-sm mt-1">{error}</div>}
      {infoText && (
        <div className="text-[12px] text-gray-500 mt-1">{infoText}</div>
      )}
    </div>
  );
};

export default DatePicker;
